// libs
import { Box, InputLabel, Typography } from '@mui/material';
import Link from 'next/link';
import { Button } from '@foxtons/fdl/components/Button/index';

import { Dispatch, SetStateAction, useState } from 'react';
// src

import { calculateBorrowFunc, numberWithCommas } from '@utils/helperFunctions';
// styles
import styles from './styles';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldUnderlined from '@foxtons/fdl/components/TextFieldUnderlined/index';

export interface CalculateBorrowProps {
  fieldOneLabel: string;
  fieldTwoLabel: string;
  calculateButtonLabel: string;
  reCalculateButtonLabel: string;
  resultText1: string;
  resultText2: string;
  buttonLink: {
    label: string;
    href: string;
  };
  setHasResult?: Dispatch<SetStateAction<boolean>>;
}

const schema = z.object({
  applicantOne: z
    .string()
    .regex(/^\d*$/, 'Invalid amount')
    .max(20, 'Invalid amount')
    .nonempty({ message: 'Applicant one income is required' })
    .refine((value) => Number(value) !== 0, { message: 'Invalid amount' }),

  applicantTwo: z
    .string()
    .regex(/^\d*$/, 'Invalid amount')
    .max(20, 'Invalid amount')
});

export default function CalculateBorrow({
  buttonLink,
  calculateButtonLabel,
  fieldOneLabel,
  fieldTwoLabel,
  reCalculateButtonLabel,
  resultText1,
  resultText2,
  setHasResult
}: CalculateBorrowProps) {
  const [calculationResult, setCalculationResult] = useState({
    totalWithDeposit: '',
    totalBorrow: '',
    totalIncome: ''
  });
  const { formState, watch, handleSubmit, reset, setValue, trigger } = useForm({
    defaultValues: {
      applicantOne: '',
      applicantTwo: ''
    },
    resolver: zodResolver(schema)
  });

  const onSubmit = () => {
    const values = watch();

    setCalculationResult(
      calculateBorrowFunc(
        Number(values?.applicantOne || ''),
        Number(values?.applicantTwo || '')
      )
    );

    if (setHasResult) {
      setHasResult(true);
    }
  };

  const getFormattedValue = (value: string) => {
    const result =
      value !== '' && value.match(/^\d*$/)
        ? numberWithCommas(Number(value.replace(/,/g, '')))
        : value;

    return result;
  };

  const handleChange = (
    name: 'applicantOne' | 'applicantTwo',
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setValue(name, e.target.value?.replace(/,/g, ''), {
      shouldDirty: true,
      shouldTouch: true
    });
    trigger(name);
  };

  const reCalculate = () => {
    setCalculationResult({
      totalBorrow: '',
      totalWithDeposit: '',
      totalIncome: ''
    });

    reset();

    if (setHasResult) {
      setHasResult(false);
    }
  };

  const propertiesRoute = () => {
    const amount = Number(calculationResult.totalBorrow.replace(/,/g, ''));
    const filter = amount + amount * 0.1;

    return `${buttonLink.href}?price_to=${filter}`;
  };

  const calculator = (
    <Box>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Box sx={styles.fieldsBox}>
          <Box id="field-one">
            <InputLabel
              sx={styles.label}
              htmlFor="applicantOne"
              variant="bold-large"
            >
              {fieldOneLabel}
            </InputLabel>
            <TextFieldUnderlined
              variant="standard"
              sx={styles.textfield}
              startAdornmentOption
              startInputAdornment="£"
              type="text"
              value={getFormattedValue(watch('applicantOne'))}
              onChange={(e) => handleChange('applicantOne', e)}
              inputProps={{ id: 'applicantOne' }}
              error={Boolean(
                formState.errors?.applicantOne?.message?.toString()
              )}
              helperText={formState.errors?.applicantOne?.message?.toString()}
            />
          </Box>
          <Box id="field-two">
            <InputLabel
              sx={styles.label}
              htmlFor="applicantTwo"
              variant="bold-large"
            >
              {fieldTwoLabel}
            </InputLabel>
            <TextFieldUnderlined
              variant="standard"
              sx={styles.textfield}
              startAdornmentOption
              startInputAdornment="£"
              type="text"
              value={getFormattedValue(watch('applicantTwo'))}
              onChange={(e) => handleChange('applicantTwo', e)}
              inputProps={{ id: 'applicantTwo' }}
              error={Boolean(
                formState.errors?.applicantTwo?.message?.toString()
              )}
              helperText={formState.errors?.applicantTwo?.message?.toString()}
            />
          </Box>
        </Box>
        <Button type="submit" variant="primary" sx={styles.calculateBtn}>
          {calculateButtonLabel}
        </Button>
      </form>
    </Box>
  );

  const calcResult = (
    <Box display="flex" flexDirection="column" width="100%">
      <Typography sx={styles.resultText} variant="h4" width="100%">
        {resultText1}
      </Typography>
      <Typography sx={styles.calcResult} variant="h2" width="100%">
        {`£${calculationResult.totalBorrow}`}
      </Typography>
      <Typography sx={styles.resultText} variant="h4" width="100%">
        {`${resultText2} £${calculationResult.totalIncome}`}
      </Typography>
      <Box sx={styles.calcActionBox}>
        <Link
          prefetch={false}
          href={propertiesRoute()}
          style={{ textDecoration: 'none' }}
        >
          <Button variant="primary" sx={styles.calculatedResultBtn}>
            {buttonLink.label}
          </Button>
        </Link>
        <Button
          onClick={reCalculate}
          variant="secondary"
          sx={styles.calculatedResultBtn}
        >
          {reCalculateButtonLabel}
        </Button>
      </Box>
    </Box>
  );

  return calculationResult.totalBorrow ? calcResult : calculator;
}
